"use client"

import { Heart, LayoutGrid, Sparkles } from "lucide-react"
import { Icon } from "@/shared/components/icons"
import { ImageWithFallback } from "@/shared/components/image-with-fallback"
import { ImageFallback } from "@/shared/components/ui/image-fallback"
import { LocalizedLink } from "@/shared/components/i18n/localized-link"
import type { GalleryPost } from "@/shared/lib/supabase/mappers"
import type { Character } from "@/shared/types/character"
import { useTranslations } from "next-intl"
import { useLocalizedCharacters } from "@/features/characters/hooks/use-localized-character"

const GRID_CLASS = "grid grid-cols-3 gap-0.5 sm:gap-1 lg:grid-cols-4 xl:grid-cols-5"
const SKELETON_COUNT = 15

interface CreationsGridProps {
  posts: GalleryPost[]
  isLoading: boolean
}

export function CreationsGrid({ posts, isLoading }: CreationsGridProps) {
  const t = useTranslations("profile.grid")
  if (isLoading) return <GridSkeleton />

  if (posts.length === 0) {
    return (
      <EmptyState
        icon={LayoutGrid}
        title={t("emptyCreationsTitle")}
        description={t("emptyCreationsDescription")}
      />
    )
  }

  return (
    <div className={GRID_CLASS}>
      {posts.map((post) => (
        <LocalizedLink
          key={post.id}
          href={`/explore/${post.id}`}
          className="group relative aspect-square overflow-hidden rounded-sm bg-muted lg:rounded-md"
        >
          <ImageWithFallback
            src={post.thumbnailUrl || post.mediaUrl}
            alt={post.caption || t("creationAlt")}
            fill
            className="object-cover"
            sizes="(max-width: 640px) 33vw, (max-width: 1024px) 25vw, 20vw"
            fallback={<ImageFallback className="absolute inset-0" />}
            errorScope="profile-creations-grid"
            errorExtra={{ post_id: post.id }}
          />
          <div className="absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/20" />
          {post.likes > 0 && (
            <div className="absolute bottom-1.5 left-1.5 flex items-center gap-1 rounded-full bg-black/60 px-1.5 py-0.5 text-[11px] font-medium text-white backdrop-blur-sm">
              <Icon name="heart" variant="bold" size={12} className="size-3" />
              {post.likes}
            </div>
          )}
        </LocalizedLink>
      ))}
    </div>
  )
}

interface CharacterGridProps {
  characters: Character[]
  isLoading: boolean
}

const CHARACTER_GRID_CLASS =
  "grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 lg:grid-cols-4 xl:grid-cols-5"

export function CharacterGrid({ characters, isLoading }: CharacterGridProps) {
  const t = useTranslations("profile.grid")
  const displayCharacters = useLocalizedCharacters(characters)
  if (isLoading) return <CharacterGridSkeleton />

  if (characters.length === 0) {
    return (
      <EmptyState
        icon={Sparkles}
        title={t("emptyCharactersTitle")}
        description={t("emptyCharactersDescription")}
      />
    )
  }

  return (
    <div className={CHARACTER_GRID_CLASS}>
      {displayCharacters.map((character) => (
        <LocalizedLink
          key={character.id}
          href={`/chat/${character.id}`}
          className="group relative flex flex-col overflow-hidden rounded-lg border border-border/50 bg-card transition-colors hover:border-border"
        >
          {/* Portrait image */}
          <div className="relative aspect-[3/4] overflow-hidden">
            <ImageWithFallback
              src={character.avatar}
              alt={character.name}
              fill
              className="object-cover"
              sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 20vw"
              fallback={<ImageFallback className="absolute inset-0" />}
              errorScope="profile-character-grid"
              errorExtra={{ character_id: character.id }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

            {/* Style badge */}
            <span className="absolute left-2 top-2 rounded-full bg-black/50 px-2 py-0.5 text-[10px] font-medium capitalize text-white/90 backdrop-blur-sm">
              {character.style}
            </span>

            {/* Likes on image */}
            {character.likes > 0 && (
              <div className="absolute bottom-2 right-2 flex items-center gap-1 rounded-full bg-black/50 px-1.5 py-0.5 text-[11px] font-medium text-white backdrop-blur-sm">
                <Icon name="heart" variant="bold" size={12} className="size-3" />
                {character.likes}
              </div>
            )}
          </div>

          {/* Info below image */}
          <div className="flex flex-col gap-1 p-2.5 lg:p-3">
            <p className="truncate text-sm font-semibold text-foreground">
              {character.name}
            </p>
            <p className="line-clamp-1 text-xs text-muted-foreground">
              {character.tagline}
            </p>

            {/* Personality pills */}
            {character.personality.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {character.personality.slice(0, 2).map((trait) => (
                  <span
                    key={trait}
                    className="rounded-full bg-muted px-2 py-0.5 text-[10px] capitalize text-muted-foreground"
                  >
                    {trait}
                  </span>
                ))}
                {character.personality.length > 2 && (
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
                    +{character.personality.length - 2}
                  </span>
                )}
              </div>
            )}
          </div>
        </LocalizedLink>
      ))}
    </div>
  )
}

function CharacterGridSkeleton() {
  return (
    <div className={CHARACTER_GRID_CLASS}>
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex flex-col overflow-hidden rounded-lg border border-border/50 bg-card">
          <div className="aspect-[3/4] animate-pulse bg-muted" />
          <div className="flex flex-col gap-2 p-2.5">
            <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
            <div className="h-3 w-full animate-pulse rounded bg-muted" />
            <div className="flex gap-1">
              <div className="h-4 w-12 animate-pulse rounded-full bg-muted" />
              <div className="h-4 w-10 animate-pulse rounded-full bg-muted" />
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

interface LikesGridProps {
  posts: GalleryPost[]
  isLoading: boolean
}

export function LikesGrid({ posts, isLoading }: LikesGridProps) {
  const t = useTranslations("profile.grid")
  if (isLoading) return <GridSkeleton />

  if (posts.length === 0) {
    return (
      <EmptyState
        icon={Heart}
        title={t("emptyLikesTitle")}
        description={t("emptyLikesDescription")}
      />
    )
  }

  return (
    <div className={GRID_CLASS}>
      {posts.map((post) => (
        <LocalizedLink
          key={post.id}
          href={`/explore/${post.id}`}
          className="group relative aspect-square overflow-hidden rounded-sm bg-muted lg:rounded-md"
        >
          <ImageWithFallback
            src={post.thumbnailUrl || post.mediaUrl}
            alt={post.caption || t("likedPostAlt")}
            fill
            className="object-cover"
            sizes="(max-width: 640px) 33vw, (max-width: 1024px) 25vw, 20vw"
            fallback={<ImageFallback className="absolute inset-0" />}
            errorScope="profile-likes-grid"
            errorExtra={{ post_id: post.id }}
          />
          <div className="absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/20" />
        </LocalizedLink>
      ))}
    </div>
  )
}

function EmptyState({
  icon: Icon,
  title,
  description,
}: {
  icon: typeof LayoutGrid
  title: string
  description: string
}) {
  return (
    <div className="flex flex-col items-center justify-center px-4 py-20 text-center md:px-6 lg:py-28">
      <div className="mb-4 flex size-16 items-center justify-center rounded-full border border-border bg-muted lg:size-20">
        <Icon className="size-7 text-muted-foreground lg:size-8" />
      </div>
      <p className="text-sm font-medium text-foreground lg:text-base">{title}</p>
      <p className="mt-1 text-xs text-muted-foreground lg:text-sm">{description}</p>
    </div>
  )
}

function GridSkeleton() {
  return (
    <div className={GRID_CLASS}>
      {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
        <div
          key={i}
          className="aspect-square animate-pulse rounded-sm bg-muted lg:rounded-md"
        />
      ))}
    </div>
  )
}
